import { FieldProps } from './types'

import * as React from 'react';
import { forwardRef } from 'react';

import { FormControl, FormHelperText, type SxProps, useTheme, alpha } from '@mui/material'
import { FIELD_RADIUS, MENU_PAPER_SX, fieldAccentColor, fieldBorderColor, fieldErrorColor, fieldErrorRing, fieldFill, fieldFocusRing, fieldHoverRing, FieldLabel, menuPaperChromeSx } from '@repo/ui'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FieldValues, RegisterOptions } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';

interface DatePickerFieldProps<T extends FieldValues = FieldValues>
  extends Omit<FieldProps<T>, 'startAdornment'> {
  name: string
  label?: string
  placeholder?: string
  defaultValue?: string | Date | Dayjs
  helperText?: string
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  labelAdornment?: React.ReactNode
  rules?: RegisterOptions
  readOnly?: boolean
  disabled?: boolean
  sx?: SxProps
  onValueChange?: (value: string | null) => void
  format?: string
  disablePast?: boolean
  disableFuture?: boolean
  minDate?: Dayjs
  maxDate?: Dayjs
  views?: ('day' | 'month' | 'year')[]
  openTo?: 'day' | 'month' | 'year'
}

const DatePickerFieldImpl = forwardRef<HTMLInputElement, DatePickerFieldProps>(({
  label,
  name,
  placeholder,
  startAdornment,
  endAdornment,
  rules,
  register,
  control,
  helperText,
  formState: { errors },
  readOnly = false,
  disabled = false,
  defaultValue = '',
  sx,
  onValueChange,
  format = 'MM/DD/YYYY',
  disablePast = false,
  disableFuture = false,
  minDate,
  maxDate,
  views = ['year', 'month', 'day'],
  openTo = 'day',
}, ref) => {

  const theme = useTheme()
  // Hoisted rather than called four times inline below: one value, one place, and it
  // keeps the three error-border rules provably identical to each other.
  const errorColor = fieldErrorColor(theme)
  const registerProps = register(name, rules);

  // Convert defaultValue to Dayjs object
  const getDefaultValue = (): Dayjs | null => {
    if (!defaultValue) return null;
    if (typeof defaultValue === 'string') {
      return dayjs(defaultValue);
    }
    if (defaultValue instanceof Date) {
      return dayjs(defaultValue);
    }
    if (dayjs.isDayjs(defaultValue)) {
      return defaultValue;
    }
    return null;
  };

  const [value, setValue] = React.useState<Dayjs | null>(getDefaultValue());

  // See the sibling `TextField` for the reasoning. Gated on `error.type`, not on the
  // message text, so rewording `fieldCannotBeEmpty` cannot silently break it.
  const fieldError = errors[name] as { type?: string } | undefined
  const isRequiredError = fieldError?.type === 'required'
  // Label-gated: a marker with no label to sit beside renders orphaned on its own line.
  const showRequiredMarker = Boolean(label) && (Boolean(rules?.required) || isRequiredError)

  const adornmentText = fieldError
    ? (showRequiredMarker ? 'required' : undefined)
    : (helperText ?? (showRequiredMarker ? 'required' : undefined))

  // Drop the message below ONLY when the marker actually renders to replace it, so a
  // labelless required field never loses both signals at once.
  const suppressRequiredMessage = isRequiredError && showRequiredMarker

  const handleChange = (newValue: Dayjs | null) => {
    setValue(newValue);
    
    if (onValueChange) {
      onValueChange(newValue ? newValue.format(format) : null);
    }
    
    // Convert the value to an event-like object for react-hook-form
    const event = { 
      target: { 
        value: newValue ? newValue.format(format) : '',
        name: name 
      } 
    };
    registerProps.onChange(event);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl error={!!errors[name]} fullWidth>
        <FieldLabel
          label={label}
          /*
            Two message slots, one rule — see the long note in the sibling `TextField`.
            The adornment carries persistent state (`(required)` / a hint), never a
            validation message; the helper slot below carries the reason and suppresses
            ONLY the required error.

            The marker goes red exactly when it IS the active required error. Both fields
            now also carry a red border of their own, so in both the marker reinforces an
            existing signal rather than being the only one — it carries the REASON, which
            a border cannot (the same red serves "required", "too long", "bad URL"). This
            note used to say the date field was the only one with a border; that stopped
            being true when `@repo/ui`'s `Input` gained its own error rule. Kept identical
            to `TextField` either way: the two sit side by side in the same forms
            (Manufacture date next to SKU), and a marker that reddened on one and not the
            other is exactly the drift §31 exists to prevent.
          */
          adornment={adornmentText ? <FormHelperText error={isRequiredError} sx={{ m: 0 }}>({adornmentText})</FormHelperText> : undefined}
        />
        <DatePicker
          value={value}
          onChange={handleChange}
          format={format}
          disablePast={disablePast}
          disableFuture={disableFuture}
          minDate={minDate}
          maxDate={maxDate}
          views={views}
          openTo={openTo}
          readOnly={readOnly}
          disabled={disabled}
          closeOnSelect={true}
          slotProps={{
            textField: {
              placeholder,
              size: 'small',
              variant: 'outlined',
              fullWidth: true,
              inputProps: label ? { 'aria-label': label } : undefined,
              error: !!errors[name],
              // SPREAD CONDITIONALLY. Do not write `endAdornment` unconditionally.
              //
              // MUI mounts the DatePicker's own open-calendar button INTO
              // `InputProps.endAdornment`. Passing that key at all replaces it —
              // and since almost no caller supplies one, the key was present and
              // `undefined`, which silently deleted the button from every date
              // field in the app.
              //
              // What was left was a masked TEXT input with no way to open the
              // calendar, so a date could only be typed into a `YYYY-MM-DD` mask
              // one segment at a time. Typing into it out of order produced things
              // like `2012-09-DD` — a half-filled mask that reads as a real date.
              // Reported on the demo Extend modal; the product Manufacture Date
              // uses this same component and had the same defect.
              InputProps: {
                ...(startAdornment ? {startAdornment} : {}),
                ...(endAdornment ? {endAdornment} : {}),
              },
              sx: {
                "& .MuiOutlinedInput-root": {
                  // fieldChrome, not primary[50]/primary[100]. Those are the DARK tokens, so in
                  // light this field rendered a noticeably heavier grey fill and a darker border
                  // than the text fields beside it — visible in the Filters panel, where a
                  // "Select date" sat directly under a "Search" and the two disagreed about what
                  // an input looks like.
                  borderRadius: `${FIELD_RADIUS}px`,
                  backgroundColor: fieldFill(theme),
                  // Explicit height so it matches the 49px of every other field. The MUI date
                  // input sizes from its own content and came out short next to them.
                  minHeight: 49,
                  "& fieldset": {
                    borderColor: fieldBorderColor(theme),
                  },
                  // HOVER — brand border + a soft glow, guarded for both `disabled`
                  // AND `readOnly` (a read-only date, unlike most read-only text
                  // fields in this app, is tracked as its own boolean here rather
                  // than folded into `disabled` upstream — see the component's
                  // `readOnly` prop — so it needs its own explicit check).
                  "&:hover fieldset": {
                    borderColor: disabled || readOnly ? fieldBorderColor(theme) : fieldAccentColor(theme),
                  },
                  "&:hover": {
                    boxShadow: disabled || readOnly ? "none" : fieldHoverRing(theme),
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: fieldAccentColor(theme),
                  },
                  "&.Mui-focused": {
                    boxShadow: fieldFocusRing(theme),
                  },
                  /*
                    INVALID. Reconciled with `@repo/ui`'s `Input`, which now owns the same
                    three states (`&.Mui-error` there) — these two fields sit side by side
                    in the same forms (Manufacture date next to SKU), so a difference here
                    is visible in one glance.

                    Only the RESTING half existed before, and it disagreed with the shared
                    Input in both of the other states: `&:hover fieldset` and
                    `&.Mui-focused fieldset` set the BRAND border unconditionally, and
                    `&.Mui-focused` set the BRAND ring — so an errored date field went
                    purple on hover, and on focus showed a red border inside a purple halo.
                    (The border survived only because `&.Mui-error fieldset` and
                    `&.Mui-focused fieldset` are both (0,2,1) and this one is emitted
                    later; the ring, at (0,2,0) against nothing, simply won.) Both are now
                    stated explicitly rather than left to emission order.

                    An outlined field draws its border on a `<fieldset>` child while a
                    filled one draws it on the root, which is why the border rules carry a
                    ` fieldset` and the ring rule does not — see the note in `fieldChrome`.
                  */
                  "&.Mui-error fieldset": {
                    borderColor: errorColor,
                  },
                  "&.Mui-error:hover fieldset": {
                    borderColor: errorColor,
                  },
                  // No brand glow on an errored field on hover either — same
                  // "the state a field is IN outranks the state it is being
                  // touched in" rule the shared `Input` documents. (0,3,0) against
                  // the plain `&:hover`'s (0,2,0), so this wins regardless of
                  // emission order.
                  "&.Mui-error:hover": {
                    boxShadow: "none",
                  },
                  "&.Mui-error.Mui-focused fieldset": {
                    borderColor: errorColor,
                  },
                  "&.Mui-error.Mui-focused": {
                    boxShadow: fieldErrorRing(theme),
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: 16,
                  padding: '12px 14px',
                },
                ...sx,
              },
            },
            /*
              THE CALENDAR ICON — `text.secondary`, the same value every other field
              adornment in the app uses.

              It was rendering PURE WHITE (`rgb(255,255,255)`), measured on this form:
              brighter than `text.primary` itself (#fafafa) and brighter than anything
              else on the page, so a decorative affordance was the loudest element in a
              form full of real content. The sibling Search field's adornment sits at
              `rgb(161,161,170)`, and that is what this now matches.

              Nothing was setting white deliberately — MUI's `IconButton` inherits
              `color: inherit` from the adornment, which lands on the input's own text
              colour. The fix is to state the adornment colour rather than inherit the
              VALUE colour, because an icon that opens a picker is chrome, not content.

              `text.secondary` is #a1a1aa in BOTH modes (see `theme.ts`), so this needs no
              mode branch — unlike the hardcoded `hsla()` values further down.
            */
            openPickerButton: {
              sx: { color: 'text.secondary' },
            },
            /*
              THE POPUP CARD — the house floating-surface chrome, per working-conventions §69.

              This slot did not exist before, which is the entire bug the user reported as
              "no gap from the field, and lighter than every other field dropdown". §69's
              mechanism explains both symptoms at once: `theme.ts` puts the app's paper
              treatment on `MuiMenu.styleOverrides.paper`, MUI keys `styleOverrides` on the
              COMPONENT NAME, and a picker popup is a `Popover`'s Paper — not a `Menu`. So
              the override never reached it, the paper kept MUI's dark-mode ELEVATION
              OVERLAY (a lightening gradient on every elevated Paper), and it had no trigger
              gap because nothing had given it one.

              BOTH spreads are required and they are not interchangeable — the trap §69
              records against itself:

                MENU_PAPER_SX        SPACING — the 8px trigger gap, paper insets, max height
                menuPaperChromeSx    SURFACE — `backgroundImage: 'none'` (the load-bearing
                                     line that kills the overlay), the divider edge,
                                     shadows[3], the 12px house radius

              Spreading only the first fixes the gap and leaves the pale panel exactly as it
              was, which is precisely the mistake §69 was rewritten to stop.

              The two `& .MuiPaper-root` rules that used to sit in the `popper` sx below —
              `borderRadius: 2` and `boxShadow: shadows[3]` — are GONE rather than kept
              alongside this. They were a partial hand-roll of the same surface reaching it
              through a descendant selector, and leaving them would have meant two
              declarations fighting over the radius (8px there against the house 12px here)
              with the winner decided by specificity rather than by intent.

              Deliberately NOT `dateRangeDesktopPaperSx`, which solves the same problem for
              the range picker in the filters drawer. Copying it here would be the second
              hand-roll §36 exists to prevent, and it predates §69 — so it is now the
              outlier: it sets an 8px radius ("the radius of the field this drops out of")
              where the house value for a floating paper is 12px, and it sets no shadow, so
              its Paper keeps Popover's default elevation 8. Migrating it is a visual change
              to a different surface and is deliberately not done here; see the report.
            */
            desktopPaper: {
              sx: {
                ...MENU_PAPER_SX,
                ...menuPaperChromeSx(theme),
                /*
                  ⚠️ `mt: 0` OVERRIDES `MENU_PAPER_SX`'s 8px trigger gap, and the gap is
                  re-established on the popper below instead. Spread order is load-bearing
                  here — this must come AFTER the spread, exactly as the sidebar menus do
                  it (§69 sanctions the override for a surface that does not open downward).

                  A margin-top is DIRECTION-BLIND, and a date field is the case where that
                  matters. `MENU_PAPER_SX` was written for menus that drop DOWN, where
                  `mt` reads as "gap below the trigger". This picker flips above the field
                  whenever the field sits low in the viewport — which the Manufacture date
                  field on `/products/create` does — and there a top margin pushes the
                  paper DOWN, i.e. INTO the trigger it is supposed to clear.

                  Measured with `mt` left in place, picker flipped above: paper bottom to
                  field top = **0px**. Flush. So the very complaint this change was made to
                  fix ("no gap from the field") was still live on the one placement the
                  screenshot happened to show, while looking correct in the downward case.

                  The popper `offset` modifier is applied along the placement axis, so it
                  yields 8px in BOTH directions from one declaration.
                */
                mt: 0,
              },
            },
            popper: {
              /*
                The trigger gap, direction-aware — see the note on `desktopPaper`'s `mt: 0`.
                `offset: [skidding, distance]`; 8 is `MENU_PAPER_SX`'s value, restated here
                only because a margin cannot express it for a flipping surface.
              */
              modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
              sx: {
                "& .MuiYearCalendar-root": {
                  width: '100%'
                },
                "& .MuiMonthCalendar-root": {
                  width: '100%'
                },
                "& .MuiPickersArrowSwitcher-button": {
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                },
                "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                },
                "& .MuiPickersCalendarHeader-switchViewButton": {
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                },
                "& .MuiPickersDay-today": {
                  border: '1px solid hsla(271, 81%, 56%, 1)',
                  borderRadius: '50%',
                },
                "& .MuiPickersDay-root.Mui-selected": {
                  backgroundColor: 'hsla(0, 0%, 0%, 1)',
                  color: 'hsla(0, 0%, 100%, 1)',
                  "&:hover": {
                    backgroundColor: 'hsla(0, 0%, 0%, 1)',
                  },
                  "&:focus": {
                    backgroundColor: 'hsla(0, 0%, 0%, 1)',
                  },
                },
              },
            },
          }}
        />
        {errors[name] && !suppressRequiredMessage && (
          <FormHelperText sx={{ margin: '4px 0 0' }}>
            {errors[name]?.message as string}
          </FormHelperText>
        )}
      </FormControl>
    </LocalizationProvider>
  )
})

/**
 * Re-exported under the generic signature so a caller's own form type flows through.
 * See the `FieldProps` docblock in `./types` for why the implementation above stays on the
 * default instantiation and why this assertion is the standard idiom rather than a
 * suppression. `forwardRef` erases type parameters, so the ref is restored by hand here.
 */
export const DatePickerField = DatePickerFieldImpl as <T extends FieldValues = FieldValues>(
  props: DatePickerFieldProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => React.ReactElement | null
